import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";

const getLabel = (option) =>
  !option ? "" : typeof option === "string" ? option : option.label || "";

const DynamicAutocomplete = ({
  label,
  onChange,
  onInputChange,
  onOpen,
  options = [],
  loading,
  value,
  placeholder,
  onBlur,
  error,
  errorMessage,
  sx = {},
  customHeight,
  noOptionsText = "No options...",
  minCharsForSearch = 0,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState(getLabel(value));
  const [open, setOpen] = useState(false);
  const safeOptions = Array.isArray(options) ? options : [];
  const controlledValue = value ?? null;
  const valueLabel = getLabel(controlledValue);
  useEffect(() => {
    setInputValue(valueLabel);
  }, [valueLabel]);

  const handleInputChange = (event, newInputValue, reason) => {
    setInputValue(newInputValue);
    if (minCharsForSearch > 0) {
      setOpen(newInputValue.length >= minCharsForSearch);
    }
    if (onInputChange) {
      onInputChange(event, newInputValue, reason);
    }
  };

  const handleChange = (_, newValue) => {
    setInputValue(getLabel(newValue));
    if (onChange) {
      onChange(newValue);
    }
  };

  const controlledOpenProps =
    minCharsForSearch > 0
      ? {
          open,
          onOpen: (e) => {
            if (inputValue.length >= minCharsForSearch) {
              setOpen(true);
              if (onOpen) onOpen(e);
            }
          },
          onClose: () => {
            setOpen(false);
            if (onBlur) onBlur();
          },
        }
      : {
          onOpen: (e) => {
            if (onOpen) onOpen(e);
          },
          onClose: () => {
            if (onBlur) onBlur();
          },
        };

  return (
    <Autocomplete
      options={safeOptions}
      getOptionLabel={(option) => getLabel(option)}
      isOptionEqualToValue={(option, val) => option?.value === val?.value}
      filterOptions={(x) => x}
      loading={loading}
      value={controlledValue}
      inputValue={inputValue}
      noOptionsText={
        minCharsForSearch > 0 && inputValue.length < minCharsForSearch
          ? `Type at least ${minCharsForSearch} characters to search`
          : noOptionsText
      }
      sx={{
        ...sx,
        ".MuiInputBase-root": {
          width: "100%",
          height: customHeight ? customHeight : "3rem",
          gap: "8px",
          padding: "0",
          borderRadius: "12px",
          marginTop: "6px",
          marginBottom: "10px",
          fontSize: "13px",
          background: "none",
          "&.Mui-focused": {
            borderColor: "var(--header__color)",
          },
        },
        ".MuiInputBase-input::placeholder": {
          color: "#757575",
          opacity: 1,
        },
        "& .MuiAutocomplete-clearIndicator": {
          visibility: "visible",
          color: "var(--header__color)",
        },
        "& .MuiAutocomplete-clearIndicator:hover": {
          color: "var(--primary__color)",
        },
      }}
      componentsProps={{
        popper: {
          sx: {
            "& .MuiAutocomplete-noOptions": {
              color: "#6860ee",
            },
            "& .MuiAutocomplete-loading": {
              color: "#6860ee",
              fontStyle: "italic",
            },
          },
        },
      }}
      onInputChange={handleInputChange}
      renderOption={(props, option) => {
        const { key, ...liProps } = props;
        return (
          <li {...liProps} key={option.value}>
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={errorMessage}
          onKeyDown={(e) => e.stopPropagation()}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    color="primary"
                    size={20}
                    sx={{
                      color: "#6860ee",
                    }}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      onChange={handleChange}
      {...controlledOpenProps}
      {...rest}
    />
  );
};

export default DynamicAutocomplete;
